/* @jsx h */
import { defineComponent, ref, h, onMounted } from "vue";
import { LemmonModal } from "@lemmonhq/uikit";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import { api } from "@app/data/api";

interface CreditNodeModalProps {
  onClose: () => void;
  referencePaymentId: number;
  onSubmit: (data: {
    amount: number;
    notes?: string;
    payment_method: string;
    reference_payment_id: number;
  }) => Promise<void>;
}

export const CreditNodeModal = defineComponent<CreditNodeModalProps>({
  name: "CreditNodeModal",
  props: ["onClose", "onSubmit", "referencePaymentId"],
  setup(props) {
    const auth = useAuth();
    const amount = ref(0);
    const notes = ref<string>();
    const paymentMethod = ref("cash");
    const loading = ref(false);
    // A sale paid partly by gift card can be refunded back onto the card.
    // The table-credit part is refunded as "Unused table credit": nothing goes
    // back to the guest, the revenue is re-booked.
    const paidByGiftCard = ref(0);
    const paidByTableCredit = ref(0);

    onMounted(async () => {
      try {
        const payment = await api.getPayment(props.referencePaymentId);
        const rewards = payment.rewards;
        paidByTableCredit.value = Number(rewards?.table_credit_total ?? 0);
        paidByGiftCard.value =
          Number(rewards?.gift_card_total ?? 0) - paidByTableCredit.value;
      } catch {
        paidByGiftCard.value = 0;
        paidByTableCredit.value = 0;
      }
    });

    const handleSubmit = async () => {
      try {
        loading.value = true;
        await props.onSubmit({
          amount: amount.value,
          notes: notes.value,
          reference_payment_id: props.referencePaymentId,
          payment_method: paymentMethod.value,
        });
      } catch (e) {
      } finally {
        loading.value = false;
      }
    };
    const { t } = useLocalization();
    return () => (
      <LemmonModal
        title={t("reports.tx.credit_note.title")}
        onClose={props.onClose}
      >
        <div class="p-6 space-y-4">
          <div>
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text text-accent">
                  {t("general.words.amount")} (
                  {auth.user.value?.restaurant.currency || "CHF"})
                </span>
              </div>
              <input
                type="number"
                step="0.01"
                class="input input-bordered w-full"
                value={amount.value}
                onInput={(e) =>
                  (amount.value = parseFloat(
                    (e.target as HTMLInputElement).value,
                  ))
                }
              />
            </label>
          </div>
          <div>
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text text-accent">
                  {t("general.words.payment_method")}
                </span>
              </div>
              <select
                class="select select-bordered w-full"
                value={paymentMethod.value}
                onChange={(e) =>
                  (paymentMethod.value = (e.target as HTMLSelectElement)
                    .value as
                    | "cash"
                    | "card"
                    | "manual"
                    | "gift_card"
                    | "table_credit")
                }
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="manual">Manual</option>
                {paidByGiftCard.value > 0 && (
                  <option value="gift_card">
                    {t("reports.tx.credit_note.gift_card")}
                  </option>
                )}
                {paidByTableCredit.value > 0 && (
                  <option value="table_credit">
                    {t("reports.tx.credit_note.unused_table_credit")}
                  </option>
                )}
              </select>
            </label>
          </div>

          <div>
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text text-accent">
                  {t("general.words.notes")}
                </span>
              </div>
              <textarea
                class="textarea textarea-bordered w-full"
                value={notes.value}
                onInput={(e) =>
                  (notes.value = (e.target as HTMLTextAreaElement).value)
                }
                rows={4}
              />
            </label>
          </div>

          <div class="flex justify-end gap-2">
            <button class="btn btn-ghost" onClick={props.onClose}>
              {t("general.words.cancel")}
            </button>
            <button
              class="btn btn-primary"
              onClick={handleSubmit}
              disabled={
                !Number.isFinite(amount.value) ||
                amount.value <= 0 ||
                loading.value
              }
            >
              {t("reports.tx.credit_note.create_refund_button")}
            </button>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
